import { forwardRef } from 'react'
import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { cx } from '../cx'
import { Icon, type IconName } from '../Icon'

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'ghost'
  | 'danger'
  | 'buy'
  | 'quiet'
  | 'outline-strong'

export type ButtonSize = 'sm' | 'md' | 'lg'

const SIZE_CLASS: Record<ButtonSize, string> = { sm: 'btn-sm', md: '', lg: 'btn-lg' }

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
  variant?: ButtonVariant
  size?: ButtonSize
  block?: boolean
  loading?: boolean
  icon?: IconName
  iconEnd?: IconName
  type?: 'button' | 'submit' | 'reset'
  /** Render as a router link. The visual is identical. */
  to?: string
  children?: ReactNode
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'secondary',
    size = 'md',
    block,
    loading,
    icon,
    iconEnd,
    className,
    children,
    disabled,
    to,
    type = 'button',
    ...rest
  },
  ref,
) {
  const cls = cx(
    'btn',
    `btn-${variant}`,
    SIZE_CLASS[size],
    block && 'btn-block',
    loading && 'btn-loading',
    className,
  )
  const iconSize = size === 'sm' ? 'sm' : 'md'
  const content = (
    <>
      {icon && <Icon name={icon} size={iconSize} />}
      {children}
      {iconEnd && <Icon name={iconEnd} size={iconSize} />}
    </>
  )

  if (to && !disabled && !loading) {
    return (
      <Link className={cls} to={to} {...(rest as Record<string, unknown>)}>
        {content}
      </Link>
    )
  }

  return (
    <button ref={ref} type={type} className={cls} disabled={disabled || loading} {...rest}>
      {content}
    </button>
  )
})

export interface IconButtonProps extends Omit<ButtonProps, 'icon' | 'children' | 'iconEnd'> {
  icon: IconName
  /** Becomes the accessible name. An icon button without one is unusable. */
  label: string
  /** Draws the unread pip the topbar bell carries. */
  pip?: boolean
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { icon, label, pip, variant = 'ghost', size = 'md', className, ...rest },
  ref,
) {
  return (
    <Button
      ref={ref}
      variant={variant}
      size={size}
      className={cx('btn-icon', className)}
      aria-label={label}
      title={label}
      style={pip ? { position: 'relative' } : undefined}
      {...rest}
    >
      <Icon name={icon} size={size === 'sm' ? 'sm' : 'md'} />
      {pip && (
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            insetBlockStart: 5,
            insetInlineEnd: 5,
            inlineSize: 6,
            blockSize: 6,
            borderRadius: 99,
            background: 'var(--c-sell)',
          }}
        />
      )}
    </Button>
  )
})
